import MenuItem from "@mui/material/MenuItem";
import type { SelectChangeEvent } from "@mui/material/Select";
import Select from "@mui/material/Select";

import { InputContainer } from "../InputContainer";

type Props<T extends Record<string, unknown>> = {
  placeholder: string;
  identifier?: string;
  value?: string;
  onChange?: (changes: string, identifier?: string) => void;
  required?: boolean;
  label?: string;
  alignLabel?: boolean;
  disabled?: boolean;
  options: T[];
  firstOptionDisabled?: boolean;
  optionTextProperty: keyof T;
  optionValueProperty: keyof T;
  renderValueFn?: (options: T) => JSX.Element | JSX.Element[];
  renderItemsFn?: (options: T[]) => JSX.Element | JSX.Element[];
};

export function SelectInput<T extends Record<string, unknown>>(props: Props<T>) {
  const {
    placeholder,
    value,
    onChange,
    label,
    optionTextProperty,
    optionValueProperty,
    identifier = undefined,
    required = false,
    alignLabel = false,
    disabled = false,
    firstOptionDisabled = false,
    options,
    renderValueFn,
    renderItemsFn,
  } = props;

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: "fit-content",
      },
    },
  };

  const handleChange = (event: SelectChangeEvent) => {
    if (!onChange) return;

    const eventValue = event.target.value;
    onChange(eventValue, event.target.name ?? undefined);
  };

  return (
    <InputContainer required={required} label={label} alignLabel={alignLabel}>
      <Select
        displayEmpty
        size="small"
        disabled={disabled}
        value={value}
        variant="filled"
        name={identifier}
        onChange={handleChange}
        sx={{
          width: 150,
          height: 30,
          fontSize: 14,
          paddingLeft: "0.5em",
          borderRadius: 1, // 4px
          outline: "none",
          border: 0,
          paddingTop: "0.6em",
          paddingBottom: "0.6em",
          "&:before, &:after": {
            borderBottom: "none",
          },
          "&:hover:before, &:hover:after": {
            borderBottom: "none",
          },
        }}
        MenuProps={MenuProps}
        inputProps={{ "aria-label": "Without label" }}
        renderValue={(selected) => {
          if (selected && options.length) {
            const found = options.find((opt) =>
              opt[optionValueProperty] ? `${opt[optionValueProperty]}` === selected : null
            );
            if (found) {
              if (renderValueFn) return renderValueFn(found);
              return found[optionTextProperty];
            }
            return <>{placeholder}</>;
          }
          return <>{placeholder}</>;
        }}
      >
        <MenuItem disabled={firstOptionDisabled} value={undefined}>
          {placeholder}
        </MenuItem>
        {renderItemsFn
          ? renderItemsFn(options)
          : options.map((option, index) => {
              const optionValue = `${option[optionValueProperty]}`;
              const optionText = `${option[optionTextProperty]}`;
              return (
                <MenuItem
                  key={`select-list-${optionValue}-${index}-${optionText}`}
                  value={optionValue}
                  sx={
                    {
                      // uncomment and adjust to your color if you want custom selected background
                      // '&.Mui-selected': {
                      //   backgroundColor: 'yourColorHere !important',
                      // },
                    }
                  }
                >
                  {optionText}
                </MenuItem>
              );
            })}
      </Select>
    </InputContainer>
  );
}
